/**
 * RaceCountdownOverlay — full-screen countdown during the staging phase.
 *
 * Shows a large countdown number (10, 9, ... 1) when the event is in
 * the 'staging' phase. The number comes from the announcement's
 * secondsRemaining, which the server broadcasts every second during
 * the staging countdown.
 *
 * Only visible when:
 *   - There is an active GM event in the 'staging' phase
 *   - The current map is the race course (gm_race_course_1)
 *
 * @see docs/design/gm/GM_RACE_MVP_PTR_CARD.md
 */

import React from 'react';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS } from '../../theme';

function getCurrentMapId(): string {
  return (globalThis as any).nm?.getRoom?.()?.state?.mapId ?? '';
}

export const RaceCountdownOverlay: React.FC = () => {
  const { announcement } = useGmEvent();

  if (!announcement || announcement.phase !== 'staging') return null;

  // Only show on the race course (not the lobby — the lobby HUD handles
  // the pre-warp wait).
  const mapId = getCurrentMapId();
  if (!mapId.startsWith('gm_race_course')) return null;

  const seconds = announcement.secondsRemaining;
  if (seconds <= 0 || seconds > 10) return null;

  // Scale: bigger numbers for lower counts (drama).
  const fontSize = seconds <= 3 ? 120 : 80;
  const color = seconds <= 3 ? COLORS.danger : COLORS.textAccent;

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.4)',
    zIndex: 500,
    pointerEvents: 'none',
  };

  const numberStyle: React.CSSProperties = {
    fontSize,
    fontWeight: 'bold',
    color,
    fontFamily: FONTS.ui,
    textShadow: '0 0 20px rgba(0,0,0,0.8), 0 4px 8px rgba(0,0,0,0.6)',
    animation: 'raceCountdownPulse 1s ease-out',
  };

  return (
    <div style={overlayStyle}>
      <style>{`
        @keyframes raceCountdownPulse {
          0% { transform: scale(1.5); opacity: 0; }
          30% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div style={numberStyle}>{seconds}</div>
    </div>
  );
};
